import * as React from 'react'
import { useMemo } from 'react'
import { Divider, mergeClasses, Text, Tooltip } from '@fluentui/react-components'
import { Payment16Filled, Payment16Regular } from '@fluentui/react-icons'
import {
    BaseInvoiceTurnoverDetails,
    InvoiceCurrencyOverviewCount,
    InvoiceHeaderItemProps,
    InvoiceLineItemProps,
    useItemStyles,
} from './InvoiceOverview'
import { useOverviewStyles } from '../../BaseOverview/BaseOverview'
import { InvoiceType } from '../../../config/finance/invoiceConfig'
import MoneyText from '../../../components/Text/Currency/MoneyText'
import useCurrency from '../../../hooks/useCurrency'

function InvoiceListItemTurnoverCurrency(props: (InvoiceLineItemProps | InvoiceHeaderItemProps) & { currency: InvoiceCurrencyOverviewCount }) {
    const itemStyles = useItemStyles()
    const { currency } = useCurrency(props.currency.currency)

    return (
        <div className={itemStyles.turnoverContainer}>
            <Text>Subtotaal</Text>
            <Text>{currency?.symbol}</Text>
            <MoneyText amount={props.currency.subtotal} hideCurrency={true} textProps={{ size: 300 }} align="right" />
            {props.currency.vat && (
                <>
                    <Text>BTW</Text>
                    <Text>{currency?.symbol}</Text>
                    <MoneyText amount={props.currency.vat} hideCurrency={true} textProps={{ size: 300 }} align="right" />
                </>
            )}
            <Text>Totaal</Text>
            <Text>{currency?.symbol}</Text>
            <MoneyText amount={props.currency.total} hideCurrency={true} textProps={{ size: 300 }} align="right" />
            <Divider vertical={false} />
            <Divider vertical={false} />
            <Divider vertical={false} />
            <Text>Betaald</Text>
            <Text>{currency?.symbol}</Text>
            <MoneyText amount={props.currency.paidPrice} hideCurrency={true} textProps={{ size: 300 }} align="right" />
            <Text>Nog te betalen</Text>
            <Text>{currency?.symbol}</Text>
            <MoneyText amount={props.currency.outstandingTotal} hideCurrency={true} textProps={{ size: 300 }} align="right" />
            {props.currency.type === InvoiceType.Deposit && (
                <>
                    <Divider vertical={false} appearance={'strong'} />
                    <Divider vertical={false} appearance={'strong'} />
                    <Divider vertical={false} appearance={'strong'} />
                    <Text>Nog te verrekenen</Text>
                    <Text>{currency?.symbol}</Text>
                    <MoneyText amount={props.currency.unsettledTotal} hideCurrency={true} textProps={{ size: 300 }} align="right" />
                </>
            )}
        </div>
    )
}

export default function InvoiceListItemTurnover(props: InvoiceLineItemProps | InvoiceHeaderItemProps) {
    const overviewStyles = useOverviewStyles()
    const itemStyles = useItemStyles()

    const turnover: BaseInvoiceTurnoverDetails = useMemo(() => (props['item'] ? props['item'] : props['count']), [props])

    const paymentComponent = (item: InvoiceCurrencyOverviewCount) => (
        <>
            {item.isPartiallyPaid && <Payment16Regular />}
            {item.isPaid && <Payment16Filled />}
            {!item.isPartiallyPaid && !item.isPaid && <div className={itemStyles.listInvoiceItemPaidIcon}></div>}
        </>
    )

    return (
        <Tooltip
            content={
                <div className={itemStyles.turnoversContainer}>
                    {turnover?.currencies?.map((currency, index) => (
                        <InvoiceListItemTurnoverCurrency key={index} {...props} currency={currency} />
                    ))}
                </div>
            }
            relationship={'description'}
            positioning={'above-end'}
            withArrow
        >
            <div className={mergeClasses(overviewStyles.flexColumn)}>
                {turnover?.currencies?.map((currency, index) => (
                    <div key={index} className={mergeClasses(overviewStyles.flexRow, overviewStyles.flexJustifyEnd)}>
                        {props.viewportMedium && paymentComponent(currency)}
                        <MoneyText
                            value={{
                                currency: currency.currency,
                                amount: currency.total,
                            }}
                            textProps={{
                                size: 300,
                            }}
                            align="right"
                        />
                        {!props.viewportMedium && paymentComponent(currency)}
                    </div>
                ))}
            </div>
        </Tooltip>
    )
}
